/**
 * Central tunables for FLOWSTATE. Keep gameplay-affecting numbers here so the
 * "feel" can be tuned in one place. The simulation runs in a fixed *logical*
 * coordinate space (ARENA_W x ARENA_H) that is scaled/letterboxed to the screen.
 */

// --- Arena ----------------------------------------------------------------
// Portrait, mobile-first. The whole battle happens inside this fixed field;
// it is letterboxed onto any screen so patterns are identical everywhere.
export const ARENA_W = 720;
export const ARENA_H = 1280;

// --- Simulation -----------------------------------------------------------
export const TICK_HZ = 60; // fixed-timestep simulation rate
export const TICK_DT = 1 / TICK_HZ; // seconds per sim step
export const MAX_FRAME_DT = 0.1; // clamp huge frame gaps (tab unfocus)

// --- Player ---------------------------------------------------------------
export const PLAYER = {
  radius: 16, // visual radius
  hitRadius: 5, // tiny hitbox (bullet-hell convention)
  grazeRadius: 34, // near-miss ring for graze scoring
  // Movement easing toward the input target (per-second smoothing factor).
  // Higher = snappier. Tuned for weighty-but-responsive feel.
  followLerp: 22,
  maxSpeed: 1400, // logical units/sec cap
  // On touch, the avatar floats this far ABOVE the finger so it isn't occluded.
  touchOffsetY: 60,
  invulnTime: 1.2, // seconds of i-frames after a hit
  startLives: 5,
};

// --- Heart pickup -----------------------------------------------------------
// A floating bonus that drifts across the arena and restores a lost life on
// touch. Only spawns while the player is missing a life, so it always reads
// as a positive thing to look forward to rather than clutter.
export const HEART = {
  radius: 16,
  speed: 90, // drift speed, logical units/sec
  life: 11, // seconds on screen before despawning if uncollected
  minInterval: 20, // seconds
  maxInterval: 40, // seconds
  pickupRadius: 30, // generous collection radius around the player center
};

// --- Sword (tap to slash) ---------------------------------------------------
export const SWORD = {
  radius: 130, // slash reach around the player
  cooldown: 0.45, // seconds between slashes
  fxTime: 0.3, // visual spin duration (one full blade revolution)
};

// --- Hazard bars (the sweeping pink rectangles) ---------------------------
// Bars are deliberately SHORT so a single one never walls off the whole arena —
// the player can always route around it. They come in three discrete speed
// tiers for rhythmic variety, and levels introduce them one at a time.
export const BAR = {
  thickness: 30, // short-axis size (was 44-66); much slimmer than before
  length: 300, // long-axis span; finite, well under the arena dimensions
  speeds: [240, 330, 420] as const, // three tiers: slow / medium / fast (~30% slower)
};

// --- Per-level difficulty --------------------------------------------------
// A speed multiplier applied to every hazard's velocity (dots, pillars, darts,
// bombs) and to meteor entry speed — faster projectiles = less reaction time =
// harder. Indexed by level (0-based): L2 +20%, L3 +40%, L4 +60%; L1 and the
// L5 boss stay at base so their hand-tuned pacing is untouched.
export const LEVEL_DIFFICULTY: readonly number[] = [1, 1.2, 1.4, 1.6, 1];

// --- Juice ----------------------------------------------------------------
export const JUICE = {
  hitShake: 22,
  grazeShake: 3,
  hitStopTime: 0.12, // brief time-dilation on hit (seconds, realtime)
  hitStopScale: 0.05, // sim speed during hit-stop
};

// --- Pools (preallocated; zero per-frame allocation) ----------------------
export const POOL = {
  bullets: 4096,
  enemies: 256,
  particles: 2048,
  hearts: 8,
};

export const PARTICLE_BUDGET = 1400; // soft cap for low-end devices

// --- Enemies ---------------------------------------------------------------
export const ENEMY = {
  spawnGrace: 0.5, // seconds after spawn before an enemy's body can hit
  deathTime: 0.45, // death animation duration (s)
};

// --- Dragon boss -------------------------------------------------------------
export const DRAGON = {
  segments: 14, // body segments behind the head
  headRadius: 30,
  tailRadius: 8,
  spacing: 24, // follow-the-leader spacing between segments
  cruiseSpeed: 330, // phase-1 head speed (scaled up per phase)
  diveSpeed: 980,
  hp: 25, // sword hits to fell it early, before the timeline runs out
  deathTime: 1.4, // death-animation duration (s) — grander than a regular enemy
};

// --- Palette (flat, high-contrast vector aesthetic) -----------------------
export const COLOR = {
  bg: 0x05060a,
  star: 0x2a3450,
  player: 0x36f9d0,
  playerCore: 0xffffff,
  graze: 0x36f9d0,
  hazard: 0xff2e63,
  hazardSoft: 0xff6b9d,
  telegraph: 0xff2e63,
  bullet: 0xff5b7f,
  accent: 0xffd166,
  heart: 0x4fc3ff,
  text: 0xeaf2ff,
  dim: 0x5a6478,
  // Enemy identity colors (one per archetype).
  enemyMeteor: 0xc97b4a,
  enemyMeteorFlame: 0xff9e2c,
  enemyArcher: 0xb388ff,
  enemyBomber: 0x8fa88a,
  enemyHen: 0xfff4e0,
  enemyHenComb: 0xff4757,
  enemyBeast: 0xa64ca6,
  enemyLaser: 0xff5b7f,
  enemyLaserCore: 0xffe9f0,
  enemyNinja: 0x46527a,
  enemyNinjaScarf: 0xff4757,
  sword: 0x7ec8ff,
  // Dragon boss.
  dragonBody: 0xe8542f,
  dragonBelly: 0xffb35c,
  dragonFin: 0xffd166,
  dragonFire: 0xffa136,
  dragonEye: 0xfff06a,
};
